import React, { useEffect, useState } from 'react';
import { Button } from './Button';
import MoodSphere from './MoodSphere';
import MentalWeather from './MentalWeather';
import MicroFeedback from './MicroFeedback';
import { HeartPulseIcon, BookOpenIcon, HeadphonesIcon, MessageSquareIcon, ArrowRightIcon, ActivityIcon, BrainIcon, HistoryIcon, CheckCircleIcon, SparklesIcon, CloudIcon, CloudRainIcon, SunIcon, PlusIcon, InfoIcon, UserIcon, PhoneIcon, CalendarIcon } from 'lucide-react';
import { useUserData } from '../context/UserDataContext';
interface HomeProps {
  onStartMoment: () => void;
  onQuickCheckIn: (mood: any) => void;
  onStartQuestionnaire?: () => void;
  onExploreThemes?: () => void;
  onOpenSoundscapes?: () => void;
  onOpenDiary?: () => void;
  onStartExercise?: (exerciseId: string) => void;
  setCurrentView: (view: string) => void;
}
const Home: React.FC<HomeProps> = ({
  onStartMoment,
  onQuickCheckIn,
  onStartQuestionnaire,
  onExploreThemes,
  onOpenSoundscapes,
  onOpenDiary,
  onStartExercise,
  setCurrentView
}) => {
  const [showMoodSphere, setShowMoodSphere] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  // Use the shared context
  const {
    mentalState,
    userProgress,
    recentActivities,
    updateMentalState
  } = useUserData();
  useEffect(() => {
    const timer = setTimeout(() => setAnimateIn(true), 100);
    return () => clearTimeout(timer);
  }, []);
  const handleMoodCapture = mood => {
    // Update energy and positivity based on mood coordinates
    const updatedEnergy = 100 - mood.y; // Invert Y axis for energy
    const updatedPositivity = mood.x; // X axis for positivity
    // Update the shared context
    updateMentalState({
      energy: updatedEnergy,
      positivity: updatedPositivity,
      lastCheckIn: 'Zojuist'
    });
    onQuickCheckIn(mood);
    setShowMoodSphere(false);
  };
  const handleAIInsightClick = (index: number) => {
    // Show feedback based on which insight was clicked
    if (index === 0) {
      alert('Analyse van je slaappatroon wordt geopend...');
      setCurrentView('feed');
    } else if (index === 1) {
      alert('Wandelen verhoogt je stemming! We hebben een wandelmeditatie voor je klaarstaan.');
      onStartExercise && onStartExercise('focus-1');
    }
  };
  const handleProgressClick = (index: number) => {
    // Navigate based on which progress item was clicked
    if (index === 0) {
      alert('Je hebt deze oefening al voltooid. Wil je hem opnieuw doen?');
      onStartExercise && onStartExercise('stress-1');
    } else if (index === 1) {
      onOpenDiary && onOpenDiary();
    }
  };
  const getWeatherIcon = () => {
    // Based on both energy and positivity
    const {
      energy,
      positivity
    } = mentalState;
    if (energy > 80 && positivity > 70) return <SunIcon className="w-12 h-12 text-gray-700" data-id="element-464" />;
    if (energy > 60 && positivity > 50) return <CloudIcon className="w-12 h-12 text-gray-700" data-id="element-465" />;
    if (energy < 40 && positivity < 30) return <CloudRainIcon className="w-12 h-12 text-gray-700" data-id="element-466" />;
    return <CloudIcon className="w-12 h-12 text-gray-700" data-id="element-467" />;
  };
  const getWeatherDescription = () => {
    // Based on both energy and positivity
    const {
      energy,
      positivity
    } = mentalState;
    if (energy > 80 && positivity > 70) return 'Helder met zonnige momenten';
    if (energy > 60 && positivity > 50) return 'Licht bewolkt met opklaringen';
    if (energy < 40 && positivity < 30) return 'Bewolkt met enkele buien';
    if (energy < 30 && positivity > 50) return 'Rustig en kalm';
    if (energy < 50 || positivity < 50) return 'Licht bewolkt met enkele buien';
    return 'Afwisselend bewolkt met heldere momenten';
  };
  return <div className={`w-full max-w-xl mx-auto py-6 space-y-8 transition-opacity duration-500 ${animateIn ? 'opacity-100' : 'opacity-0'}`} data-id="element-468">
      {/* Welcome section with subtle animation */}
      <div className="px-2 transform transition-all duration-500 ease-out translate-y-0" data-id="element-469">
        <h1 className="text-2xl font-light text-gray-800 mb-1" data-id="element-470">Hallo Lisa</h1>
        <p className="text-sm text-gray-600" data-id="element-471">
          Laatste check-in: {mentalState.lastCheckIn}
        </p>
      </div>

      {/* Combined Check-in and Mental Weather Card */}
      <div className="border-2 border-gray-300 rounded-xl overflow-hidden" data-id="element-472">
        <div className="p-6 bg-gray-50 border-b border-gray-200" data-id="element-473">
          <div className="flex items-center justify-between mb-4" data-id="element-474">
            <div className="flex items-center" data-id="element-475">
              {getWeatherIcon()}
              <div className="ml-4" data-id="element-476">
                <h2 className="text-lg font-medium text-gray-800" data-id="element-477">
                  Jouw mentale weer
                </h2>
                <p className="text-sm text-gray-600" data-id="element-478">
                  {getWeatherDescription()}
                </p>
              </div>
            </div>
            <button className="p-2 rounded-full hover:bg-gray-100" onClick={onStartQuestionnaire} aria-label="Meer informatie" data-id="element-479">
              
            </button>
          </div>
          <div className="space-y-4 mb-6" data-id="element-480">
            <div data-id="element-481">
              <div className="flex justify-between text-sm text-gray-600 mb-1" data-id="element-482">
                <span data-id="element-483">Energie</span>
                <span data-id="element-484">{Math.round(mentalState.energy)}%</span>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-485">
                <div className="h-full bg-gray-600 rounded-full transition-all duration-700 ease-in-out" style={{
                width: `${mentalState.energy}%`
              }} data-id="element-486"></div>
              </div>
            </div>
            <div data-id="element-487">
              <div className="flex justify-between text-sm text-gray-600 mb-1" data-id="element-488">
                <span data-id="element-489">Positiviteit</span>
                <span data-id="element-490">{Math.round(mentalState.positivity)}%</span>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-491">
                <div className="h-full bg-gray-600 rounded-full transition-all duration-700 ease-in-out" style={{
                width: `${mentalState.positivity}%`
              }} data-id="element-492"></div>
              </div>
            </div>
          </div>
          <div className="flex justify-center" data-id="element-493">
            <Button onClick={() => setShowMoodSphere(true)} className="transition-transform duration-200 hover:scale-105" data-id="element-494">
              Check-in bijwerken
            </Button>
          </div>
        </div>
        <div className="p-5 border-t border-gray-200" data-id="element-495">
          <div className="p-4 border border-gray-200 rounded-lg bg-white hover:bg-gray-50 cursor-pointer transition-colors duration-200" onClick={onStartQuestionnaire} data-id="element-496">
            <div className="flex items-start" data-id="element-497">
              <SparklesIcon className="w-4 h-4 text-gray-600 mt-0.5 mr-2 flex-shrink-0" data-id="element-498" />
              <div data-id="element-499">
                <p className="text-sm text-gray-600 mb-1" data-id="element-500">
                  {mentalState.energy > 60 && mentalState.positivity > 60 ? 'Je voelt je energiek en positief' : mentalState.energy < 40 && mentalState.positivity < 40 ? 'Je voelt je mogelijk wat gespannen en somber' : 'Je stemming is in balans'}
                </p>
                <p className="text-sm text-gray-700 font-medium" data-id="element-501">
                  Tip:{' '}
                  {mentalState.energy > 60 && mentalState.positivity > 60 ? 'Focus op productieve taken' : mentalState.energy < 40 && mentalState.positivity < 40 ? 'Een korte ademhalingsoefening kan helpen' : 'Blijf mindfull gedurende de dag'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick access grid - expanded to 3x2 with hover effects */}
      <div data-id="element-502">
        <h2 className="text-lg font-medium text-gray-800 mb-3 px-2" data-id="element-503">
          Snel toegang
        </h2>
        <div className="grid grid-cols-2 gap-3 mb-3" data-id="element-504">
          <button className="p-4 border-2 border-gray-300 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-all duration-200 transform hover:scale-[1.02] flex flex-col items-center text-center touch-manipulation" onClick={onExploreThemes} data-id="element-505">
            <div className="w-12 h-12 rounded-full border-2 border-gray-400 flex items-center justify-center mb-2" data-id="element-506">
              <BookOpenIcon className="w-6 h-6 text-gray-600" data-id="element-507" />
            </div>
            <span className="text-sm font-medium text-gray-800" data-id="element-508">
              Thema's & Oefeningen
            </span>
          </button>
          <button className="p-4 border-2 border-gray-300 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-all duration-200 transform hover:scale-[1.02] flex flex-col items-center text-center touch-manipulation" onClick={onOpenDiary} data-id="element-509">
            <div className="w-12 h-12 rounded-full border-2 border-gray-400 flex items-center justify-center mb-2" data-id="element-510">
              <MessageSquareIcon className="w-6 h-6 text-gray-600" data-id="element-511" />
            </div>
            <span className="text-sm font-medium text-gray-800" data-id="element-512">Dagboek</span>
          </button>
          <button className="p-4 border-2 border-gray-300 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-all duration-200 transform hover:scale-[1.02] flex flex-col items-center text-center touch-manipulation" onClick={onOpenSoundscapes} data-id="element-513">
            <div className="w-12 h-12 rounded-full border-2 border-gray-400 flex items-center justify-center mb-2" data-id="element-514">
              <HeadphonesIcon className="w-6 h-6 text-gray-600" data-id="element-515" />
            </div>
            <span className="text-sm font-medium text-gray-800" data-id="element-516">
              Soundscapes
            </span>
          </button>
          <button className="p-4 border-2 border-gray-300 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-all duration-200 transform hover:scale-[1.02] flex flex-col items-center text-center touch-manipulation" onClick={() => setCurrentView('favorites')} data-id="element-517">
            <div className="w-12 h-12 rounded-full border-2 border-gray-400 flex items-center justify-center mb-2" data-id="element-518">
              <ActivityIcon className="w-6 h-6 text-gray-600" data-id="element-519" />
            </div>
            <span className="text-sm font-medium text-gray-800" data-id="element-520">
              Favorieten
            </span>
          </button>
        </div>
        
      </div>

      {/* AI Insights - New section */}
      <div className="border-2 border-gray-300 rounded-xl p-5" data-id="element-521">
        <div className="flex items-center mb-3" data-id="element-522">
          <SparklesIcon className="w-5 h-5 text-gray-700 mr-2" data-id="element-523" />
          <h2 className="text-lg font-medium text-gray-800" data-id="element-524">AI Inzichten</h2>
        </div>
        <div className="space-y-3" data-id="element-525">
          <div className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors duration-200" onClick={() => handleAIInsightClick(0)} data-id="element-526">
            <p className="text-sm text-gray-800 mb-1" data-id="element-527">
              Je slaapt de laatste tijd minder goed op maandagen
            </p>
            <p className="text-xs text-gray-500" data-id="element-528">
              Gebaseerd op 4 weken check-in data
            </p>
          </div>
          <div className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors duration-200" onClick={() => handleAIInsightClick(1)} data-id="element-529">
            <p className="text-sm text-gray-800 mb-1" data-id="element-530">
              Wandelen verhoogt je stemming met gemiddeld 20%
            </p>
            <p className="text-xs text-gray-500" data-id="element-531">
              Ontdek meer patronen in je data
            </p>
          </div>
        </div>
      </div>

      {/* Recent progress with timeline styling */}
      <div className="border-2 border-gray-300 rounded-xl p-5" data-id="element-532">
        <div className="flex justify-between items-center mb-4" data-id="element-533">
          <h2 className="text-lg font-medium text-gray-800" data-id="element-534">Jouw voortgang</h2>
          <button className="text-sm text-gray-600 hover:text-gray-800" onClick={() => setCurrentView('feed')} data-id="element-535">
            Alles bekijken
          </button>
        </div>
        <div className="relative pl-6 border-l-2 border-gray-200 border-dashed space-y-6" data-id="element-536">
          {recentActivities.map((activity, index) => <div key={activity.id} className="relative cursor-pointer" onClick={() => handleProgressClick(index)} data-id="element-537">
              <div className="absolute -left-[22px] top-0 w-4 h-4 rounded-full bg-white border-2 border-gray-400" data-id="element-538"></div>
              <div data-id="element-539">
                <p className="text-sm font-medium text-gray-800" data-id="element-540">
                  {activity.title}
                </p>
                <p className="text-xs text-gray-500" data-id="element-541">{activity.timestamp}</p>
              </div>
            </div>)}
        </div>
      </div>

      {/* Direct contact section - New */}
      <div className="border-2 border-gray-300 rounded-xl p-5" data-id="element-542">
        <div className="flex items-center mb-4" data-id="element-543">
          <PhoneIcon className="w-5 h-5 text-gray-700 mr-2" data-id="element-544" />
          <h2 className="text-lg font-medium text-gray-800" data-id="element-545">Direct contact</h2>
        </div>
        <p className="text-sm text-gray-600 mb-4" data-id="element-546">
          Heb je behoefte aan persoonlijk contact met een therapeut? Dat kan
          direct, zonder wachttijd.
        </p>
        <div className="grid grid-cols-2 gap-3" data-id="element-547">
          <button className="p-3 border-2 border-gray-300 rounded-xl hover:bg-gray-50 transition-colors duration-200 flex flex-col items-center" onClick={() => setCurrentView('therapist-chat')} data-id="element-548">
            <MessageSquareIcon className="w-5 h-5 text-gray-700 mb-2" data-id="element-549" />
            <span className="text-sm font-medium text-gray-800" data-id="element-550">Chat</span>
          </button>
          <button className="p-3 border-2 border-gray-300 rounded-xl hover:bg-gray-50 transition-colors duration-200 flex flex-col items-center" onClick={() => alert('Video gesprek functie komt binnenkort beschikbaar')} data-id="element-551">
            <PhoneIcon className="w-5 h-5 text-gray-700 mb-2" data-id="element-552" />
            <span className="text-sm font-medium text-gray-800" data-id="element-553">
              Video gesprek
            </span>
          </button>
        </div>
      </div>

      {/* Crisis help button - more prominent */}
      <div className="px-2" data-id="element-554">
        <button className="w-full p-4 border-2 border-gray-300 rounded-xl bg-gray-50 hover:bg-gray-100 active:bg-gray-200 transition-colors duration-200 flex items-center justify-center touch-manipulation" onClick={() => setCurrentView('help')} data-id="element-555">
          <HeartPulseIcon className="w-5 h-5 text-gray-700 mr-2" data-id="element-556" />
          <span className="text-sm font-medium text-gray-800" data-id="element-557">Hulp nodig?</span>
        </button>
      </div>

      {/* Mood check modal with enhanced animation */}
      {showMoodSphere && <div className="fixed inset-0 bg-white bg-opacity-95 z-50 flex flex-col items-center justify-center p-4 animate-fadeIn" data-id="element-558">
          <div className="w-full max-w-md bg-white border-2 border-gray-300 rounded-xl p-6 shadow-md" data-id="element-559">
            <h2 className="text-xl font-medium text-gray-800 mb-2 text-center" data-id="element-560">
              Hoe voel je je op dit moment?
            </h2>
            <p className="text-sm text-gray-600 mb-6 text-center" data-id="element-561">
              Selecteer je stemming op de cirkel hieronder
            </p>
            <div className="flex justify-center mb-10" data-id="element-562">
              <MoodSphere onCapture={handleMoodCapture} size="large" data-id="element-563" />
            </div>
            <div className="flex justify-between" data-id="element-564">
              <Button variant="secondary" onClick={() => setShowMoodSphere(false)} data-id="element-565">
                Annuleren
              </Button>
              <Button onClick={onStartMoment} leftIcon={<BrainIcon className="w-4 h-4" data-id="element-567" />} data-id="element-566">
                Uitgebreide check-in
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-4 text-center" data-id="element-568">
              De snelle check-in registreert je stemming. De uitgebreide
              check-in geeft meer inzicht in je mentale welzijn.
            </p>
          </div>
        </div>}

      {showFeedback && <MicroFeedback message="Bedankt voor je check-in. We hebben je suggesties aangepast." onClose={() => setShowFeedback(false)} data-id="element-569" />}
    </div>;
};
export default Home;